import React, { createContext, useContext, useEffect } from "react";
import { Outlet } from "react-router-dom";

interface ExperimentProperty {
    propertyName: string;
    defaultValue: any;
}

interface ExperimentContextProps {
    experimentId: number | null;
    variantId: number | null;
    properties: Record<string, any> | null;
    setExperiments: (dictionary: Record<string, any> | null) => void;
    fetchProperty: (experimentProperty: ExperimentProperty) => any;
    isInExperiment: (id: number) => boolean;
}

export const ExperimentProperties = {
    isPaidOnly: { propertyName: "is_paid_only", defaultValue: false }
};

const ExperimentContext = createContext<ExperimentContextProps | undefined>(undefined);

export const ExperimentProvider = () => {
    const [experimentId, setExperimentId] = React.useState<number | null>(null);
    const [variantId, setVariantId] = React.useState<number | null>(null);
    const [properties, setProperties] = React.useState<Record<string, any> | null>(null);
    const [isExperimentsLoaded, setIsExperimentsLoaded] = React.useState<boolean>(false);


    const resetExperiments = () => {
        setExperimentId(null);
        setVariantId(null);
        setProperties(null);
    };

    const setExperiments = (dictionary: Record<string, any> | null) => {
        if (dictionary) {
            resetExperiments();
            
            let propertyOnlyDictionary = { ...dictionary };
            
            if (dictionary.experiment_id !== undefined) {
                setExperimentId(dictionary.experiment_id);
                delete propertyOnlyDictionary.experiment_id;
            }
            
            if (dictionary.variant_id !== undefined) {
                setVariantId(dictionary.variant_id);
                delete propertyOnlyDictionary.variant_id;
            }
            
            setProperties(propertyOnlyDictionary);
        } else {
            // If no dictionary is provided, we could load from some default source
            // For now, we'll just set the loaded flag
        }
        
        setIsExperimentsLoaded(true);
    };

    const fetchProperty = (experimentProperty: ExperimentProperty): any => {
        if (!isExperimentsLoaded) {
            setExperiments(null);
        }
        
        if (properties && properties[experimentProperty.propertyName] !== undefined) {
            return properties[experimentProperty.propertyName];
        }
        
        return experimentProperty.defaultValue;
    };

    const isInExperiment = (id: number): boolean => {
        return experimentId === id;
    };

    const providerValue: ExperimentContextProps = {
        experimentId,
        variantId,
        properties,
        setExperiments,
        fetchProperty,
        isInExperiment,
    };

    return <ExperimentContext.Provider value={providerValue}><Outlet /></ExperimentContext.Provider>;
};

export const useExperiment = () => {
    const context = useContext(ExperimentContext);
    if (!context) {
        throw new Error("useExperiment must be used within an ExperimentProvider");
    }
    return context;
}; 